import React from 'react';
import * as mobx from 'mobx';
import { FormattedMessage, IntlShape } from 'react-intl';
import * as IoC from 'typescript-ioc';
import { IErrorParsing } from '../../../infrastructure/error-parsing';
import { alertServerError, showNotification } from '../../global/modal/alerts-host/alert-templates';
import { IAlertsService } from '../../../infrastructure/alerts';
import { IUserDetailsDTO, IUsersDAO, UserAction } from '../../../api/users.dao';
import { Space } from '../../../constants/html-character-entities';
import { UserFormDataVM } from './user-form-data.viewmodel';
import TrackingServiceProvider from '../../../tracking-service/tracking-service-provider';
import { convertBoolTypeForMixPanel, TRACKING_EVENTS } from '../../../tracking-service/tracking-values';

class UserFormVM {
    @mobx.observable
    public saving: boolean;

    @mobx.observable
    public isLoading: boolean;

    @mobx.observable
    public userFormDataVM: UserFormDataVM;

    public username: string;

    public initialValues: IUserDetailsDTO;

    constructor(
        private fetchUsersList: () => void,
        private closeEditUserForm: (username: string) => void,
        @IoC.Inject private readonly usersDAO: IUsersDAO = IoC.Container.get(IUsersDAO),
        @IoC.Inject private readonly errorParsingService: IErrorParsing = IoC.Container.get(IErrorParsing),
        @IoC.Inject private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
        @IoC.Inject
        private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider)
    ) {
        this.isLoading = true;
    }

    public isSaving() {
        return this.saving;
    }

    public setUser = async (username?: string) => {
        if (username) {
            this.username = username;
            const userDetails = await this.usersDAO.getUser(username);
            this.initialValues = userDetails;
            mobx.runInAction(() => (this.userFormDataVM = new UserFormDataVM()));
            this.userFormDataVM.initialize(userDetails);
        } else {
            mobx.runInAction(() => (this.userFormDataVM = new UserFormDataVM()));
            this.userFormDataVM.initialize();
        }

        mobx.runInAction(() => {
            this.isLoading = false;
        });
    };

    public getFormDataVM(): UserFormDataVM {
        return this.userFormDataVM;
    }

    public getErrorMessage(intl: IntlShape): string | null {
        const {showValidationErrors, getFirstNameError, getLastNameError, getEmailError, serverErrorMessage} = this.userFormDataVM;
        if (showValidationErrors()) {
            if (getFirstNameError(intl)) {
                return getFirstNameError(intl);
            } else if (getLastNameError(intl)) {
                return getLastNameError(intl);
            } else if (getEmailError(intl)) {
                return getEmailError(intl);
            }
        }
        return serverErrorMessage;
    }

    @mobx.action
    public handleSubmit = async (intl: IntlShape, userAction: string) => {
        this.getFormDataVM().serverErrorMessage = '';
        const isFormInvalid = !this.getFormDataVM().isFormValid(intl);
        if (isFormInvalid) {
            this.getFormDataVM().allowToShowValidationErrors();
            return false;
        }
        try {
            this.saving = true;

            let userData;
            if (userAction === UserAction.Create) {
                userData = this.userFormDataVM.toApiCreateUserDTO();
                await this.usersDAO.createUser(userData);
                showNotification(
                    intl.formatMessage({
                        id: 'oe.user.userAdded',
                        description: 'Notification about added user',
                        defaultMessage: 'New user added',
                    })
                );

                this.trackingService.get().track(TRACKING_EVENTS.NEW_USER, {
                    SuperUser: convertBoolTypeForMixPanel(userData.isSuperuser),
                    'OpenExchange Staff Member': convertBoolTypeForMixPanel(userData.isOpenExchangeStaffMember),
                    'All Accounts': convertBoolTypeForMixPanel(userData.isAllAccounts),
                    'Assigned accounts': this.userFormDataVM.assignedAccountsForTracking,
                    'Account Groups': this.userFormDataVM.assignedAccountGroupsForTracking,
                    'Assigned Roles': this.userFormDataVM.assignedRolesForTracking,
                });
            } else {
                userData = this.userFormDataVM.toApiUpdateUserDTO();
                await this.usersDAO.editUser(userData, this.username);
                showNotification(
                    intl.formatMessage({
                        id: 'oe.user.userEdit',
                        description: 'Notification about edit user',
                        defaultMessage: 'User details updated',
                    })
                );

                this.trackingService.get().track(TRACKING_EVENTS.EDIT_USER, {
                    SuperUser: convertBoolTypeForMixPanel(userData.isSuperuser !== this.initialValues.isSuperuser),
                    'OpenExchange Staff Member': convertBoolTypeForMixPanel(
                        userData.isOpenExchangeStaffMember !== this.initialValues.isOpenExchangeStaffMember
                    ),
                    'All Accounts': convertBoolTypeForMixPanel(
                        userData.isAllAccounts !== this.initialValues.isAllAccounts
                    ),
                    'Assigned accounts': this.userFormDataVM.assignedAccountsForTracking,
                    'Account Groups': this.userFormDataVM.assignedAccountGroupsForTracking,
                    'Assigned Roles': this.userFormDataVM.assignedRolesForTracking,
                });
            }
            await setTimeout(() => this.fetchUsersList(), 1000);
            return true;
        } catch (error) {
            this.onCreateUserError(error);
            return false;
        } finally {
            mobx.runInAction(() => {
                this.saving = false;
            });
        }
    };

    @mobx.action
    private onCreateUserError(error): void {
        let errorMessage: string;
        errorMessage = this.errorParsingService.extractMessage(error);
        if (errorMessage) {
            this.getFormDataVM().serverErrorMessage = errorMessage;
        }
    }

    @mobx.action
    public onDeleteUser = () => {
        this.alertsService.confirm({
            message: (
                <span className='alert-message'>
                    <FormattedMessage
                        id='oe.confirm.areYouSureTo'
                        description='Confirm message'
                        defaultMessage='Are you sure you want to'
                    />
                    <Space />
                    <span className='alert-message--danger'>
                        <FormattedMessage
                            id='oe.user.deleteThisUser'
                            description='Confirm message'
                            defaultMessage='delete this user'
                        />
                    </span>
                    ?
                </span>
            ),
            confirmButtonProps: {
                text: 'Delete User',
                modifier: 'confirm-warn',
                handler: this.deleteUserApiCall,
            },
            cancelButtonProps: {
                modifier: 'cancel',
            },
        });
    };

    private completeUserDeletion = (userEmail: string) => {
        setTimeout(() => this.fetchUsersList(), 1000);
        this.closeEditUserForm(userEmail.toString());
    };

    private deleteUserApiCall = async () => {
        try {
            await this.usersDAO.deleteUser(this.username);
            this.trackingService.get().track(TRACKING_EVENTS.DELETE_USER, {});
            this.completeUserDeletion(this.username);
        } catch (error) {
            alertServerError('Error', this.errorParsingService.extractMessage(error));
        }
    };

    @mobx.action
    public onActivateUser = (): void => {
        this.alertsService.confirm({
            message: (
                <span className='alert-message'>
                    <FormattedMessage
                        id='oe.confirm.areYouSureTo'
                        description='Confirm message'
                        defaultMessage='Are you sure you want to'
                    />
                    <Space />
                    { !this.userFormDataVM.active ?
                        <span className='alert-message--danger'>
                        <FormattedMessage
                            id='oe.user.activateThisUser'
                            description='Confirm message'
                            defaultMessage='activate this user'
                        />
                    </span>
                    :
                        <span className='alert-message--danger'>
                            <FormattedMessage
                                id='oe.user.inactivateThisUser'
                                description='Confirm message'
                                defaultMessage='inactivate this user'
                            />
                        </span>
                    }
                    ?
                </span>
            ),
            confirmButtonProps: {
                text: !this.userFormDataVM.active ? 'Activate User' : 'Inactivate User',
                modifier: 'confirm-warn',
                handler: this.activateUserApiCall,
            },
            cancelButtonProps: {
                modifier: 'cancel',
            },
        });
    };

    private activateUserApiCall = async (): Promise<void> => {
        try {
            await this.usersDAO.activateUser(this.username, !this.userFormDataVM.active);
            this.userFormDataVM.toggleActiveUser();
            this.trackingService.get().track(TRACKING_EVENTS.USER_ACTIVATED_DEACTIVATED,
                {
                    'User edited': this.userFormDataVM.email,
                    'User status': this.userFormDataVM.active ? 'Active' : 'Inactive'
                });
            this.completeUserDeletion(this.username);
        } catch (error) {
            alertServerError('Error', this.errorParsingService.extractMessage(error));
        }
    };
}

export { UserFormVM };
